import { MenuService } from "./menu.services";
import { inject, Injectable, signal } from "@angular/core";

export interface MenuReorderingEdit {
  id: number;
  name: string;
}

@Injectable({
  providedIn: "root",
})
export class MenuReorderingService extends MenuService {
  //--------------------------------------------------------------------------------------------------------------------
  // Variables privées
  //--------------------------------------------------------------------------------------------------------------------
  private readonly menuService = inject(MenuService);
  readonly #swEdit = signal<boolean>(false);
  readonly #nodeEdit = signal<MenuReorderingEdit | undefined>(undefined);
  readonly #selectedId = signal<number>(0);
  readonly #nameSelected = signal<string>("");

  //--------------------------------------------------------------------------------------------------------------------
  // Variables public
  //--------------------------------------------------------------------------------------------------------------------
  swEdit = this.#swEdit.asReadonly();
  nodeEdit = this.#nodeEdit.asReadonly();
  selectedId = this.#selectedId.asReadonly();
  nameSelected = this.#nameSelected.asReadonly();

  //--------------------------------------------------------------------------------------------------------------------
  // setter
  // Mode édition
  //--------------------------------------------------------------------------------------------------------------------
  setSwEditTrue(id: number): void {
    this.#swEdit.set(true);
    const selectedNode = this.menuService
      .node()
      .find((node) => node.archiveId === id);
    const edit: MenuReorderingEdit = {
      id,
      name: selectedNode?.name ?? "",
    };
    this.#nodeEdit.set(edit);
    this.menuService.setIsSelected(id);
    this.#selectedId.set(id);
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Désactive le mode édition
  //--------------------------------------------------------------------------------------------------------------------
  setSwEditFalse(): void {
    this.#swEdit.set(false);
    this.#nodeEdit.set(undefined);
    this.menuService.setIsSelectedFalse();
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Modifie le nom d’un élément du menu à partir de son archiveId
  //--------------------------------------------------------------------------------------------------------------------
  updateMenuNodeName(name: string): void {
    const id: number = this.selectedId();
    const trimmedName = name.trim();

    if (!trimmedName) {
      return;
    }

    const nodes = this.menuService.node().map((node) =>
      node.archiveId === id
        ? {
            ...node,
            name: trimmedName,
          }
        : node,
    );

    this.menuService.setMenuNode(nodes);
    this.setSwEditFalse();
  }

  //--------------------------------------------------------------------------------------------------------------------
  // Modifie l’icône d’un élément du menu à partir de son archiveId
  // Pas besoin d'être en mode édition
  //--------------------------------------------------------------------------------------------------------------------
  updateMenuNodeIcon(
    iconName: string,
    selectedIconId: number,
  ): void {
    const id: number = selectedIconId;

    const nodes = this.menuService.node().map((node) =>
      node.archiveId === id
        ? {
            ...node,
            icon: iconName,
          }
        : node,
    );

    this.menuService.setMenuNode(nodes);
  }
}
